import React, { useEffect, useState } from "react";
import { LuX, LuBadgeCheck } from "react-icons/lu";
import { authFetch } from "../../utils/api";

function DetailRow({ label, value, source, isLink }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="lf-detail-row">
      <span className="lf-detail-label">{label}</span>
      <span className="lf-detail-value">
        {isLink ? (
          <a href={value} target="_blank" rel="noreferrer">{value}</a>
        ) : (
          value
        )}
        {source && <span className="lf-source-tag">{source}</span>}
      </span>
    </div>
  );
}

function initials(name, username) {
  const base = (name || username || "?").trim();
  return base.slice(0, 1).toUpperCase();
}

function LeadDrawer({ leadId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    authFetch(`/lead-finder/result/${leadId}`)
      .then((res) => { if (active) setData(res); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [leadId]);

  const lead = data?.lead;
  const website = data?.website;

  const socials = website
    ? [
        ["Facebook", website.facebook],
        ["LinkedIn", website.linkedin],
        ["Twitter", website.twitter],
        ["YouTube", website.youtube],
        ["Instagram", website.instagram],
      ].filter(([, url]) => url)
    : [];

  return (
    <div className="lf-drawer-overlay" onClick={onClose}>
      <div className="lf-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="lf-drawer-header">
          <button className="lf-drawer-close" onClick={onClose}><LuX /></button>
          {loading ? (
            <div className="lf-drawer-name">Loading…</div>
          ) : lead ? (
            <div className="lf-drawer-profile">
              {lead.profile_image_url ? (
                <img className="lf-drawer-avatar" src={lead.profile_image_url} alt={lead.username} referrerPolicy="no-referrer" />
              ) : (
                <div className="lf-drawer-avatar" style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700 }}>
                  {initials(lead.name, lead.username)}
                </div>
              )}
              <div>
                <div className="lf-drawer-name">
                  {lead.name || lead.username}
                  {lead.is_verified ? <LuBadgeCheck style={{ verticalAlign: "-3px", marginLeft: 6 }} /> : null}
                </div>
                <div className="lf-drawer-username">
                  {lead.source === "google_maps" ? "Google Maps" : `@${lead.username}`}
                </div>
                {lead.source === "google_maps" ? (
                  <div className="lf-stat-inline">
                    <div><strong>{lead.rating ? `${lead.rating}★` : "—"}</strong><span>Rating</span></div>
                    <div><strong>{lead.reviews ? Number(lead.reviews).toLocaleString() : "—"}</strong><span>Reviews</span></div>
                  </div>
                ) : (
                  <div className="lf-stat-inline">
                    <div><strong>{Number(lead.followers).toLocaleString()}</strong><span>Followers</span></div>
                    <div><strong>{Number(lead.following).toLocaleString()}</strong><span>Following</span></div>
                    <div><strong>{Number(lead.posts).toLocaleString()}</strong><span>Posts</span></div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="lf-drawer-name">Lead not found</div>
          )}
        </div>

        {lead && (
          <>
            <div className="lf-drawer-section">
              <h4>{lead.source === "google_maps" ? "Business Profile" : "Instagram Profile"}</h4>
              <DetailRow label="Category" value={lead.category} />
              <DetailRow label="Business Name" value={lead.business_name} />
              <DetailRow label="Type" value={lead.business_type} />
              {lead.source === "google_maps" ? (
                <>
                  <DetailRow label="Rating" value={lead.rating ? `${lead.rating} ★` : null} />
                  <DetailRow label="Reviews" value={lead.reviews != null ? String(lead.reviews) : null} />
                  <DetailRow label="Google Maps" value={lead.profile_url || lead.maps_url} isLink />
                </>
              ) : (
                <>
                  <DetailRow label="Verified" value={lead.is_verified ? "Yes" : "No"} />
                  <DetailRow label="Account" value={lead.is_private ? "Private" : "Public"} />
                  <DetailRow label="Instagram ID" value={lead.instagram_id} />
                  <DetailRow label="Profile" value={lead.profile_url} isLink />
                </>
              )}
              {lead.bio && (
                <div className="lf-detail-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                  <span className="lf-detail-label">Bio</span>
                  <span className="lf-detail-value" style={{ textAlign: "left", fontWeight: 400, marginTop: 4 }}>{lead.bio}</span>
                </div>
              )}
            </div>

            <div className="lf-drawer-section">
              <h4>Contact Details</h4>
              <DetailRow label="Email" value={lead.email} source={lead.email_source} />
              <DetailRow label="Phone" value={lead.phone} source={lead.phone_source} />
              <DetailRow label="WhatsApp" value={lead.whatsapp} />
              <DetailRow label="Website" value={lead.website} isLink />
              {!lead.email && !lead.phone && !lead.whatsapp && (
                <div className="lf-muted" style={{ fontSize: 13 }}>No public contact details found.</div>
              )}
            </div>

            {(lead.address || lead.city || lead.state || lead.country || lead.maps_url) && (
              <div className="lf-drawer-section">
                <h4>Location</h4>
                <DetailRow label="Address" value={lead.address} />
                <DetailRow label="City" value={lead.city} />
                <DetailRow label="State" value={lead.state} />
                <DetailRow label="Country" value={lead.country} />
                <DetailRow label="Google Maps" value={lead.maps_url} isLink />
              </div>
            )}

            {website && website.status === "ok" && (
              <div className="lf-drawer-section">
                <h4>Website Extraction</h4>
                <DetailRow label="Business Name" value={website.business_name} />
                {Array.isArray(website.emails) && website.emails.length > 0 && (
                  <DetailRow label="Emails" value={website.emails.join(", ")} />
                )}
                {Array.isArray(website.phones) && website.phones.length > 0 && (
                  <DetailRow label="Phones" value={website.phones.join(", ")} />
                )}
                <DetailRow label="Contact Page" value={website.contact_page} isLink />
                <DetailRow label="About Page" value={website.about_page} isLink />
                <DetailRow label="Store" value={website.store_url} isLink />
                {socials.length > 0 && (
                  <>
                    <div className="lf-detail-label" style={{ margin: "10px 0 8px" }}>Social Links</div>
                    <div className="lf-social-links">
                      {socials.map(([name, url]) => (
                        <a key={name} href={url} target="_blank" rel="noreferrer">{name}</a>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="lf-drawer-section">
              <h4>Scraping History</h4>
              <DetailRow label="Source Keyword" value={lead.source_keyword} />
              <DetailRow label="Source Location" value={lead.source_location} />
              <DetailRow label="Scraped At" value={lead.scraped_at} />
              <DetailRow label="Last Updated" value={lead.updated_at} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LeadDrawer;
